import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProductSearch = () => {
  const { currentOrgId } = useAuth();
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const debouncedQ = useDebounce(q, 250);

  useEffect(() => {
    const search = async () => {
      setError('');
      if (!currentOrgId) return;
      if (!debouncedQ || debouncedQ.trim().length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const { data, error } = await supabase
          .rpc('search_products_fuzzy', {
            search_term: debouncedQ.trim(),
            p_org_id: currentOrgId,
            p_limit: 50
          });

        if (error) throw error;
        setResults(data || []);
      } catch (e) {
        setError(e?.message || 'Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    };
    search();
  }, [debouncedQ, currentOrgId]);

  const clear = () => {
    setQ('');
    setResults([]);
    setError('');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <Input
            label="Search Products"
            placeholder="Search by SKU, name or supplier code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">Type at least 2 characters. Showing top 50 matches.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={clear}>Clear</Button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-destructive">{error}</div>
      )}

      <div className="overflow-auto border border-border rounded-md">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-muted text-muted-foreground">
              <Th>SKU</Th>
              <Th>Name</Th>
              <Th>Supplier</Th>
              <Th className="text-right">Price</Th>
              <Th className="text-right">VAT %</Th>
              <Th className="text-right">Stock</Th>
              <Th className="text-right">Case Qty</Th>
              <Th className="text-right">Case Cost</Th>
              <Th>Last Sold</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="p-3" colSpan={9}>Searching…</td></tr>
            ) : results.length === 0 ? (
              <tr><td className="p-3 text-muted-foreground" colSpan={9}>No results</td></tr>
            ) : (
              results.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                  <Td mono>{r.sku}</Td>
                  <Td>{r.name}</Td>
                  <Td mono>{r.supplier_code || ''}</Td>
                  <Td num>{fmtMoney(r.current_price)}</Td>
                  <Td num>{r.vat_rate != null ? Number(r.vat_rate).toFixed(2) : ''}</Td>
                  <Td num>{r.stock_qty != null ? Number(r.stock_qty).toFixed(2) : ''}</Td>
                  <Td num>{r.case_qty != null ? Number(r.case_qty) : ''}</Td>
                  <Td num>{r.case_cost != null ? Number(r.case_cost).toFixed(2) : ''}</Td>
                  <Td>{fmtDate(r.last_sold_at)}</Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`text-left px-3 py-2 border-b border-border sticky top-0 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, num = false }) => (
  <td className={`px-3 py-2 align-top ${mono ? 'font-mono text-xs' : ''} ${num ? 'text-right' : ''}`}>{children}</td>
);

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function fmtMoney(v) {
  if (v == null) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return `£${n.toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return iso; }
}

export default ProductSearch;
